import { useEffect, useRef, useState } from 'react';
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
} from '@chakra-ui/react';
import ApexDomain from '../apexDomain';
import { ApexDomainEmailHandle, EmailDnsUiState } from '../apexDomainEmail';
import InfoTooltip from 'src/components/atoms/info-tooltip';
import StripeConnectForm from '../../stripe-connect-form';
import { MarketplaceInfo } from 'src/api/v1-api';
import Link from 'next/link';
import useSingleToast from 'src/utils/hooks/toast/useSingleToast';

export interface IntegrationsFormFields {
  shortenedName: string;
  apexDomain: string;
  emailState: EmailDnsUiState | null;
  googleSearchConsoleVerification: string;
}

export interface IntegrationsFormProps {
  marketplace: MarketplaceInfo | null;
  isLoading?: boolean;
  onSubmit: (data: IntegrationsFormFields) => void;
}

export const IntegrationsForm = ({ marketplace, isLoading, onSubmit }: IntegrationsFormProps) => {
  const [shortenedName, setShortenedName] = useState('');
  const [apexDomain, setApexDomain] = useState('');
  const [domainStatus, setDomainStatus] = useState('');
  const [emailStatus, setEmailStatus] = useState('');
  const [digestStatus, setDigestStatus] = useState('');
  const [isIntegrationsPreDnsEdited, setIsIntegrationsPreDnsEdited] = useState(false);
  const [showDomainChangeModal, setShowDomainChangeModal] = useState(false);
  const [googleSearchConsoleVerification, setGoogleSearchConsoleVerification] =
    useState<string>('');

  const emailStateRef = useRef<EmailDnsUiState | null>(null);
  const apexEmailRef = useRef<ApexDomainEmailHandle | null>(null);
  const toastNotification = useSingleToast();

  const shortenedDefault = (marketplace?.slug ?? '').toUpperCase().substring(0, 16);
  const shortenedNameTooltip = `By default, sales through Loma will appear on credit card statements from "LOMA*" with the marketplace subdomain as a suffix. You may customize the name after "LOMA*" for clarity. There is a limit of 16 characters for the shortened name. Marketplaces that use Stripe Connect will show their full statement descriptor without "LOMA*".`;

  useEffect(() => {
    if (marketplace) {
      setShortenedName(marketplace.shortened_name);
      setGoogleSearchConsoleVerification(
        marketplace.functional?.google_search_console_verification ?? '',
      );
      if (marketplace?.domain) {
        setApexDomain(marketplace.domain.domain);
        setDomainStatus(marketplace.domain.status);
        setEmailStatus(marketplace.domain.email_status);
        setDigestStatus(marketplace.domain.digest_status);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [marketplace]);

  if (!marketplace) {
    return <div>Loading...</div>;
  }

  const handleIntegrationsChange = (state: EmailDnsUiState) => {
    emailStateRef.current = state;
    setIsIntegrationsPreDnsEdited(!!state.preDnsEdited);
  };

  const undoDomainChanges = async () => {
    apexEmailRef.current?.resetDomains();
    setShowDomainChangeModal(false);
    performSave();
  };

  const generateDnsAndContinue = async () => {
    try {
      await apexEmailRef.current?.generateChangedDns();
      setShowDomainChangeModal(false);
      performSave();
    } catch (e) {
      toastNotification({
        title: 'Error',
        description: 'Failed to generate DNS. Please try again.',
        status: 'error',
        duration: 6000,
        isClosable: true,
        position: 'bottom',
      });
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const emailState = emailStateRef.current;
    if (emailState) {
      // 1) Hide Save occurs via disabled state below if pre-DNS edited
      // 2) If username changed after DNS -> allow save but show alert after save
      // 3) If any email domain changed after DNS -> show modal to Undo or Generate DNS
      if (emailState.emailDomainChangedAfterDns || emailState.digestDomainChangedAfterDns) {
        setShowDomainChangeModal(true);
        return;
      }
    }
    performSave();
  };

  const performSave = () => {
    onSubmit({
      shortenedName,
      apexDomain,
      emailState: emailStateRef.current,
      googleSearchConsoleVerification,
    });
  };

  return (
    <form onSubmit={handleSubmit} style={{ width: '100%' }}>
      <Stack
        justify="flex-start"
        align="flex-start"
        spacing="0"
        w="100%"
        maxW="100%"
        overflow="hidden"
      >
        <Accordion allowToggle w="100%" maxW="100%">
          <AccordionItem borderBottom="none">
            <AccordionButton>
              <Box as="span" flex="1" textAlign="left">
                Google Search Console
              </Box>
              <AccordionIcon />
            </AccordionButton>
            <AccordionPanel pb={4} maxW="100%" overflow="hidden">
              <Stack spacing="20px" maxW="100%" overflow="hidden">
                <FormControl>
                  <FormLabel>Verification String</FormLabel>
                  <Text fontSize="sm" color="gray.600" mb="8px" wordBreak="break-word">
                    Use the verification string provided by Google Search Console to verify your
                    marketplace. Use the HTML tag method.
                  </Text>
                  <Input
                    placeholder="Verification String"
                    value={googleSearchConsoleVerification}
                    onChange={(e) => setGoogleSearchConsoleVerification(e.target.value)}
                    maxW="100%"
                  />
                </FormControl>
              </Stack>
            </AccordionPanel>
          </AccordionItem>

          <AccordionItem borderBottom="none">
            <AccordionButton>
              <Box as="span" flex="1" textAlign="left">
                Payment Configuration
              </Box>
              <AccordionIcon />
            </AccordionButton>
            <AccordionPanel pb={4}>
              <Stack spacing="20px">
                <FormControl>
                  <FormLabel>
                    Shortened Name for Credit Card Statements
                    <InfoTooltip description={shortenedNameTooltip} />
                  </FormLabel>
                  <Text fontSize="sm" color="gray.600" mb="8px">
                    Customize how your marketplace appears on customer credit card statements
                  </Text>
                  <Input
                    placeholder={shortenedDefault}
                    value={shortenedName}
                    onChange={(e) =>
                      setShortenedName(e.target.value.toUpperCase().substring(0, 16))
                    }
                    max={16}
                  />
                  <Text mt={2} fontSize="sm" color="gray.600">
                    Will appear as: LOMA* {shortenedName || shortenedDefault}
                    {marketplace?.functional?.stripe_connect_config?.status === 'active' && (
                      <span> - only used for peer-to-peer transactions</span>
                    )}
                  </Text>
                </FormControl>
              </Stack>
            </AccordionPanel>
          </AccordionItem>
        </Accordion>

        <ApexDomain
          apexDomain={apexDomain}
          setApexDomain={setApexDomain}
          domainStatus={domainStatus}
          emailStatus={emailStatus}
          digestStatus={digestStatus}
          apexEmailRef={apexEmailRef}
          onEmailUiChange={handleIntegrationsChange}
        />

        {marketplace?.id && (
          <StripeConnectForm
            marketplaceId={marketplace.id}
            stripeConnectConfig={marketplace?.functional?.stripe_connect_config}
          />
        )}
      </Stack>
      <Flex direction={'row'} justifyContent={'flex-end'} alignItems={'center'} pt={10}>
        <Box display="flex" justifyContent="flex-end">
          <Link href="/">
            <Button variant="outline" colorScheme="primary" mr={4}>
              Cancel
            </Button>
          </Link>
          {!isIntegrationsPreDnsEdited && (
            <Button
              isLoading={isLoading}
              colorScheme="primary"
              type="submit"
              data-test="submit-btn"
            >
              Save
            </Button>
          )}
        </Box>
      </Flex>
      <Modal
        isOpen={showDomainChangeModal}
        onClose={() => setShowDomainChangeModal(false)}
        isCentered
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Generate DNS required</ModalHeader>
          <ModalBody>
            Changing the email domain requires new DNS records. Undo your changes or generate DNS
            now.
          </ModalBody>
          <ModalFooter>
            <Button variant="outline" mr={3} onClick={undoDomainChanges}>
              Undo changes
            </Button>
            <Button colorScheme="primary" onClick={generateDnsAndContinue}>
              Generate DNS
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </form>
  );
};

export default IntegrationsForm;
